/**
 * WHMCS Connect Modal
 * Modal for entering WHMCS module credentials
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Info as InfoIcon,
  Business as BusinessIcon,
  Key as KeyIcon,
} from '@mui/icons-material';

interface WhmcsConnectModalProps {
  open: boolean;
  onClose: () => void;
  onConnect: (moduleUrl: string, moduleToken: string) => Promise<void>;
  isConnecting?: boolean;
}

const WhmcsConnectModal: React.FC<WhmcsConnectModalProps> = ({
  open,
  onClose,
  onConnect,
  isConnecting = false,
}) => {
  const [moduleUrl, setModuleUrl] = useState('');
  const [moduleToken, setModuleToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  const validateUrl = (url: string): boolean => {
    if (!url) {
      setUrlError('WHMCS URL is required');
      return false;
    }
    
    try {
      const urlObj = new URL(url);
      if (!urlObj.protocol.startsWith('http')) {
        setUrlError('URL must start with http:// or https://');
        return false;
      }
      setUrlError(null);
      return true;
    } catch {
      setUrlError('Please enter a valid URL');
      return false;
    }
  };

  const handleSubmit = async () => {
    setError(null);
    
    // Validate inputs
    if (!validateUrl(moduleUrl)) {
      return;
    }
    
    if (!moduleToken.trim()) {
      setError('Module token is required');
      return;
    }
    
    try {
      await onConnect(moduleUrl.trim(), moduleToken.trim());
      // Reset form on success
      setModuleUrl('');
      setModuleToken('');
      setShowToken(false);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to connect to WHMCS');
    }
  };

  const handleClose = () => {
    if (!isConnecting) {
      setModuleUrl('');
      setModuleToken('');
      setShowToken(false);
      setError(null);
      setUrlError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="whmcs-connect-dialog"
    >
      <DialogTitle id="whmcs-connect-dialog">
        <Box display="flex" alignItems="center" gap={1}>
          <BusinessIcon color="primary" />
          <Typography variant="h6">Connect WHMCS Analytics</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {/* Instructions Alert */}
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>To connect your WHMCS:</strong>
            </Typography>
            <Typography variant="body2" component="ol" sx={{ pl: 2, mb: 0 }}>
              <li>Install the MCP Analytics addon module in your WHMCS</li>
              <li>Go to Setup → Addon Modules → MCP Analytics</li>
              <li>Copy the Module Token from the module settings</li>
              <li>Enter your WHMCS URL and token below</li>
            </Typography>
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* WHMCS URL Input */}
          <TextField
            fullWidth
            label="WHMCS URL"
            placeholder="https://billing.example.com"
            value={moduleUrl}
            onChange={(e) => {
              setModuleUrl(e.target.value);
              if (urlError) validateUrl(e.target.value);
            }}
            onBlur={() => validateUrl(moduleUrl)}
            error={!!urlError}
            helperText={urlError || 'The URL of your WHMCS installation'}
            disabled={isConnecting}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Module Token Input */}
          <TextField
            fullWidth
            label="Module Token"
            type={showToken ? 'text' : 'password'}
            placeholder="Enter your module token"
            value={moduleToken}
            onChange={(e) => setModuleToken(e.target.value)}
            disabled={isConnecting}
            helperText="The token generated by the MCP Analytics module"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle token visibility"
                    onClick={() => setShowToken(!showToken)}
                    edge="end"
                    disabled={isConnecting}
                  >
                    {showToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Help Link */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Need help? Check the{' '}
              <Link
                href="https://github.com/mcp-platform/whmcs-mcp/wiki"
                target="_blank"
                rel="noopener noreferrer"
              >
                WHMCS MCP documentation
              </Link>
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isConnecting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isConnecting || !moduleUrl || !moduleToken}
          startIcon={isConnecting ? <CircularProgress size={16} /> : null}
        >
          {isConnecting ? 'Connecting...' : 'Connect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhmcsConnectModal;